import { FC } from 'react'
import { useLoadAllItemsQuery, useQuery } from '@sequencehq/api'
import {
  Text,
  Badge,
  EmptyState,
  GreyGrey80,
  IconReceiptTax,
  IndigoIndigo60,
  Link,
  Pill,
  Primary14SemiBold,
  SequenceMenu,
  SequenceMenuItemProps,
  SettingsFormControl,
  SettingsFormControlIcon,
  SettingsGroup,
  Flex
} from '@sequencehq/core-components'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMarkTaxCategoryAsDefault } from 'SettingsV2/components/tax-rates/hooks/useMarkTaxCategoryAsDefault'
import { SettingsPageLayout } from 'SettingsV2/components/SettingsPageLayout'
import { avalaraIntegrationConfig } from 'Integrations/integrationsConfig/avalara/avalara.integration.config'
import { SmallIcon } from 'Integrations/index'
import { AvalaraTaxCategoryMapping } from '../taxRates.types'
import { useCreateTaxCategoryWithMapping } from './hooks/useCreateTaxCategoryWithMapping'
import { useCreateAvalaraMapping } from './hooks/useCreateAvalaraMapping'
import { useRemoveAvalaraMapping } from './hooks/useRemoveAvalaraMapping'
import { useUpdateTaxCategoryName } from './hooks/useUpdateTaxCategoryName'
import AddTaxCategoryForAvalaraModal from './modals/AddTaxCategoryForAvalaraModal'
import EditTaxCategoryNameModal from './modals/EditTaxCategoryNameModal'
import AddAvalaraTaxCodeToTaxCategoryModal from './modals/AddAvalaraTaxCodeToTaxCategoryModal'

export const AvalaraSalesTaxSettings: FC = () => {
  const {
    data: taxCategoriesData,
    isError,
    isLoading
  } = useLoadAllItemsQuery(dashboardv99990101Client.getTaxCategories)

  const mappingsResult = useQuery(
    dashboardv99990101Client.getAllTaxCategoriesWithAvalaraMappings
  )

  const allExistingTaxCategories =
    taxCategoriesData?.pages.flatMap(page => page.items) ?? []

  const taxCategoriesWithAvalaraMappings: AvalaraTaxCategoryMapping[] =
    allExistingTaxCategories.map(taxCategory => {
      const mapping = mappingsResult.data?.items?.find(
        ({ taxCategoryId }) => taxCategoryId === taxCategory.id
      )

      return {
        taxCategoryId: taxCategory.id,
        taxCategoryName: taxCategory.name,
        isDefault: taxCategory.isDefault,
        taxName: mapping?.taxName,
        avalaraTaxCode: mapping?.avalaraTaxCode
      }
    })

  const createTaxCategoryWithMapping = useCreateTaxCategoryWithMapping()
  const createAvalaraMapping = useCreateAvalaraMapping()
  const removeAvalaraMapping = useRemoveAvalaraMapping()
  const markTaxCategoryAsDefault = useMarkTaxCategoryAsDefault()
  const updateTaxCategoryName = useUpdateTaxCategoryName()

  const handleUpdateTaxCategoryName = async (
    taxCategoryId: string,
    name: string
  ) => {
    const taxCategory = allExistingTaxCategories.find(
      tc => tc.id === taxCategoryId
    )
    if (taxCategory) {
      await updateTaxCategoryName(taxCategory, name)
    }
  }

  return (
    <SettingsPageLayout
      header="Sales tax"
      subheader="Manage tax categories and rates."
      isError={isError}
    >
      <SettingsGroup
        isLoading={isLoading && mappingsResult.isLoading}
        id="tax-categories"
        action={
          <Flex
            marginBottom="8px"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text
              as="h3"
              id="tax-categories"
              {...Primary14SemiBold}
              color={GreyGrey80}
            >
              Tax categories
            </Text>

            <AddTaxCategoryForAvalaraModal
              onCreate={createTaxCategoryWithMapping}
            />
          </Flex>
        }
      >
        {taxCategoriesWithAvalaraMappings.length === 0 && (
          <EmptyState
            title="No tax categories"
            icon={IconReceiptTax}
            iconSize="sm"
            body={
              <>
                You have not added any tax categories yet. Add a tax category,{' '}
                <Link
                  _hover={{ textDecoration: 'none' }}
                  to="https://docs.sequencehq.com/connectors/avalara"
                  color={IndigoIndigo60}
                  target="_blank"
                >
                  learn more
                </Link>
                .
              </>
            }
          />
        )}

        {taxCategoriesWithAvalaraMappings.map(mapping => (
          <SettingsFormControl
            key={mapping.taxCategoryId}
            label={mapping.taxCategoryName}
            helper={mapping.taxName}
            htmlFor={`tax-category-${mapping.taxCategoryId}`}
            icon={
              <SettingsFormControlIcon>
                <IconReceiptTax />
              </SettingsFormControlIcon>
            }
          >
            <Flex alignItems="center" gap="8px" paddingLeft="16px">
              {mapping.isDefault ? (
                <Badge sentiment="neutral" size="sm">
                  Default
                </Badge>
              ) : null}

              {mapping.avalaraTaxCode ? (
                <Pill
                  data-testid={`avalaraTaxCodeMappingTable.remove.${mapping.taxCategoryId}`}
                  interactive
                  onClear={() =>
                    void removeAvalaraMapping(mapping.taxCategoryId)
                  }
                >
                  {mapping.avalaraTaxCode}
                </Pill>
              ) : (
                <AddAvalaraTaxCodeToTaxCategoryModal
                  taxCategoryName={mapping.taxCategoryName}
                  onAddTaxCode={({ avalaraTaxCode }) =>
                    void createAvalaraMapping({
                      taxCategoryId: mapping.taxCategoryId,
                      avalaraTaxCode
                    })
                  }
                />
              )}

              <SequenceMenu
                variant="ghost"
                buttonSize="small"
                items={[
                  (props: Partial<SequenceMenuItemProps>) => (
                    <EditTaxCategoryNameModal
                      key="edit-name"
                      initialName={mapping.taxCategoryName}
                      onSubmit={async ({ name }) => {
                        await handleUpdateTaxCategoryName(
                          mapping.taxCategoryId,
                          name
                        )
                        return props?.setIsOpen?.(false)
                      }}
                      trigger={
                        <SequenceMenu.Item
                          data-testid={`editName.${mapping.taxCategoryName}`}
                          key="edit-name"
                          uuid="edit-name"
                          label="Edit name"
                        />
                      }
                    />
                  ),
                  ...(!mapping.isDefault
                    ? [
                        (props: Partial<SequenceMenuItemProps>) => (
                          <SequenceMenu.Item
                            data-testid={`markAsDefault.${mapping.taxCategoryName}`}
                            key="mark-as-default"
                            uuid="mark-as-default"
                            label="Mark as default"
                            onClick={() => {
                              void markTaxCategoryAsDefault(
                                mapping.taxCategoryId
                              )
                              return props?.setIsOpen?.(false)
                            }}
                          />
                        )
                      ]
                    : [])
                ]}
              />
            </Flex>
          </SettingsFormControl>
        ))}
      </SettingsGroup>

      <SettingsGroup title="Tax rates">
        <EmptyState
          title="Managed via Avalara"
          icon={AvalaraLogo}
          iconSize="sm"
          body={
            <>
              Tax rates are managed by Avalara,{' '}
              <Link
                _hover={{ textDecoration: 'none' }}
                to="https://docs.sequencehq.com/connectors/avalara#avalara"
                color={IndigoIndigo60}
                target="_blank"
              >
                learn more
              </Link>
              .
            </>
          }
        />
      </SettingsGroup>
    </SettingsPageLayout>
  )
}

const AvalaraLogo = () => {
  return <SmallIcon url={avalaraIntegrationConfig.logoUrl} />
}
